import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Copy, Mail, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface UnpaidCustomer {
  email: string;
  customerName: string;
  reason: string;
}

export function UnpaidCustomers() {
  const [isChecking, setIsChecking] = useState(false);
  const [unpaidCustomers, setUnpaidCustomers] = useState<UnpaidCustomer[]>([]);
  const [summary, setSummary] = useState<any>(null);

  const handleCheckUnpaid = async () => {
    setIsChecking(true);
    try {
      const { data, error } = await supabase.functions.invoke('get-unpaid-customers');

      if (error) {
        throw new Error(error.message);
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to check unpaid customers');
      }

      setUnpaidCustomers(data.unpaidCustomers);
      setSummary(data.summary);
      
      if (data.unpaidCustomers.length === 0) {
        toast.success('✅ All customers on your list have paid!');
      } else {
        toast.warning(`Found ${data.unpaidCustomers.length} unpaid customers`);
      }

    } catch (error) {
      console.error('Error checking unpaid customers:', error);
      toast.error('Failed to check unpaid customers');
    } finally {
      setIsChecking(false);
    }
  };

  const copyEmailList = () => {
    const emailList = unpaidCustomers.map(customer => customer.email).join('\n');
    navigator.clipboard.writeText(emailList);
    toast.success('Email list copied to clipboard!');
  };

  const copyEmailsAsCommaList = () => {
    const emailList = unpaidCustomers.map(customer => customer.email).join(', ');
    navigator.clipboard.writeText(emailList);
    toast.success('Email list copied as comma-separated values!');
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Unpaid Customers
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Button 
              onClick={handleCheckUnpaid} 
              disabled={isChecking}
              className="w-full"
            >
              {isChecking ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Checking for unpaid customers...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Check for Unpaid Customers
                </>
              )}
            </Button>

            {summary && (
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{summary.totalActiveInDatabase}</div>
                  <div className="text-sm text-muted-foreground">Active in Database</div>
                </div>
                <div className="text-center p-3 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{summary.checkedCustomers}</div>
                  <div className="text-sm text-muted-foreground">Customers Checked</div>
                </div>
                <div className="text-center p-3 bg-muted rounded-lg">
                  <div className={`text-2xl font-bold ${summary.unpaidCount === 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.unpaidCount}
                  </div>
                  <div className="text-sm text-muted-foreground">Unpaid</div>
                </div>
              </div>
            )}

            {unpaidCustomers.length > 0 && (
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Button onClick={copyEmailList} variant="outline" size="sm">
                    <Copy className="mr-2 h-4 w-4" />
                    Copy Email List
                  </Button>
                  <Button onClick={copyEmailsAsCommaList} variant="outline" size="sm">
                    <Copy className="mr-2 h-4 w-4" />
                    Copy as CSV
                  </Button>
                </div>

                <div className="space-y-2">
                  <h3 className="font-semibold">Unpaid Customer Emails:</h3>
                  <div className="bg-muted p-4 rounded-lg">
                    <div className="font-mono text-sm space-y-1">
                      {unpaidCustomers.map((customer, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <span>{customer.email}</span>
                          <Badge variant="secondary" className="text-xs">
                            {customer.reason}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}